import { createState, State, useState } from '@hookstate/core';
import { useNavigationState } from '@store/hookstate';

import { Icon as BlueIcon } from '@blueprintjs/core';
import ReactTooltip from 'react-tooltip';

import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { getStudioUpdates } from '@services/queries/studios/getStudioUpdates/getStudioUpdates';
import { useInfiniteQuery, useQuery } from 'react-query';
import { UpdateStatusType } from '@models/UpdateStatus.model';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { countUpdatesFilter } from '@utils';
import { CircleDiv } from '@components/Filters/Filter/Filter.styles';
import React, {
  createContext,
  useContext,
  useMemo,
  useState as classicUseState,
} from 'react';
import { getUpdatesFilter } from '@utils/getUpdatesFilter/getUpdatesFilter';
import { DetailsDialog } from '@components/DetailsDialog/DetailsDialog';

import { getStudioUpdatesMetrics } from '@services/queries/studios';

import { CreateUpdatesDialog } from '@components';
import { NavigationContext } from '@layout/navigation/navigation';
import { InviteUserRole } from '@models/Invite.model';
import {
  removeBackUpNewUpdates,
  removeBackUpUpdatedUpdates,
} from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { updateUpdatedAt } from '@store/redux/features/updates/updatesSlice';
import {
  UpdatesNew,
  UpdatesPrioritized,
  UpdatesCompleted,
  UpdatesDismissed,
  UpdatesAssignedToMe,
} from './components';
import { Menu } from '../../components/Filters/FilterUpdates/Menu';

import {
  Container,
  Title,
  Head,
  Body,
  TabsContainer,
  Indicator,
  Item,
  Label,
  Tabs,
  Back,
  Count,
  LabelText,
  FilterButton,
  Left,
  CreateButton,
  UpdatesSearch,
} from './Updates.styles';
import { CountLoading } from './Updates.loading';
import {
  toNewFormat,
  findNewUpdatesToRemove,
  findUpdatedUpdatesToRemove,
} from './utils';

interface IUpdatesContext {
  updateStatusId: State<string | null>;
}

export const UpdatesContext = createContext<IUpdatesContext>({
  updateStatusId: createState<string | null>(''),
});

const useInfiniteQueryParams = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const CountComponent = ({ count }: { count?: number }) => {
  if (count !== undefined) {
    return <Count>{count}</Count>;
  }

  return <CountLoading />;
};

const Updates = () => {
  const { auth } = useAppSelector();

  const { role } = useContext(NavigationContext);
  const navigationState = useNavigationState();
  const history = useHistory();

  const query = useInfiniteQueryParams();

  const page = query.get('tab');

  const [openCreateUpdates, setOpenCreateUpdates] =
    classicUseState<boolean>(false);

  const updateStatusId = useState<string | null>(null);

  const { updates, updatesOptimistic } = useAppSelector();
  const dispatch = useAppDispatch();

  const filterCount = countUpdatesFilter(updates.options);

  const search = useState<string>('');
  const hasData = useState<boolean>(false);

  const startSearch = (text?: string) => {
    search.set(text ?? search.get());
    const now = new Date();
    dispatch(updateUpdatedAt(now.toISOString()));
  };
  const stdArgs = [
    updates.options,
    updates.options.sort,
    updates.options.direction,
  ] as const;
  const updatesMetricsFilter = useMemo(
    () => getUpdatesFilter(updates.options),
    [updates.options],
  );
  const metricsQuery = useQuery(
    ['updatesMetrics', search.get(), updatesMetricsFilter],
    () =>
      getStudioUpdatesMetrics({
        id: auth.studioId,
        filter: updatesMetricsFilter,
        search: search.get(),
      }),
  );

  const updatesNewFilter = useMemo(
    () => getUpdatesFilter(...stdArgs, UpdateStatusType.NEW),
    [updates.options],
  );
  const updatesPrioritisedFilter = useMemo(
    () => getUpdatesFilter(...stdArgs, UpdateStatusType.PRIORITIZED),
    [updates.options],
  );
  const updatesDismissedFilter = useMemo(
    () => getUpdatesFilter(...stdArgs, UpdateStatusType.DISMISSED),
    [updates.options],
  );
  const updatesCompletedFilter = useMemo(
    () => getUpdatesFilter(...stdArgs, UpdateStatusType.COMPLETED),
    [updates.options],
  );
  const {
    data: newData,
    isLoading: newIsLoading,
    isError: newIsError,
    fetchNextPage: newFetchNextPage,
  } = useInfiniteQuery(
    ['updateStatuses', search.get(), updatesNewFilter, updates.updatedAt],
    async ({ pageParam = 1 }) => {
      if (auth.studioId) {
        const studioUpdates = await getStudioUpdates({
          pagination: {
            page: pageParam,
            limit: 20,
            search: search.get(),
            studioId: auth.studioId,
          },
          filter: updatesNewFilter,
        });

        return studioUpdates;
      }
      return {
        total: 0,
        currentPage: 0,
        totalPages: 0,
        hasMore: false,
        results: [],
      };
    },
    {
      onSuccess: data => {
        hasData.set(true);
        const newFormat = toNewFormat(data);
        const updatedUpdates = updatesOptimistic.backupUpdatedUpdates;
        const newUpdates = updatesOptimistic.backupNewUpdates;

        // Check for newUpdates
        const newUpdateIds = findNewUpdatesToRemove(
          newFormat.results,
          newUpdates,
        );
        dispatch(removeBackUpNewUpdates(newUpdateIds));
        // Check for updatedUpdates
        const updateIds = findUpdatedUpdatesToRemove(
          newFormat.results,
          updatedUpdates,
        );
        dispatch(removeBackUpUpdatedUpdates(updateIds));
      },
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: prioritizedData,
    isLoading: prioritizedIsLoading,
    isError: prioritizedIsError,
    fetchNextPage: prioritizedFetchNextPage,
  } = useInfiniteQuery(
    [
      'updateStatuses',
      search.get(),
      updatesPrioritisedFilter,
      updates.updatedAt,
    ],
    async ({ pageParam = 1 }) => {
      if (auth.studioId) {
        const studioUpdates = await getStudioUpdates({
          pagination: {
            page: pageParam,
            limit: 20,
            search: search.get(),
            studioId: auth.studioId,
          },
          filter: updatesPrioritisedFilter,
        });
        return studioUpdates;
      }
      return {
        total: 0,
        currentPage: 0,
        totalPages: 0,
        hasMore: false,
        results: [],
      };
    },
    {
      onSuccess: data => {
        hasData.set(true);
        const newFormat = toNewFormat(data);
        const updatedUpdates = updatesOptimistic.backupUpdatedUpdates;
        const newUpdates = updatesOptimistic.backupNewUpdates;

        // Check for newUpdates
        const newUpdateIds = findNewUpdatesToRemove(
          newFormat.results,
          newUpdates,
        );
        dispatch(removeBackUpNewUpdates(newUpdateIds));
        // Check for updatedUpdates
        const updateIds = findUpdatedUpdatesToRemove(
          newFormat.results,
          updatedUpdates,
        );
        dispatch(removeBackUpUpdatedUpdates(updateIds));
      },
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: dismissedData,
    isLoading: dismissedIsLoading,
    isError: dismissedIsError,
    fetchNextPage: dismissedFetchNextPage,
  } = useInfiniteQuery(
    ['updateStatuses', search.get(), updatesDismissedFilter, updates.updatedAt],
    async ({ pageParam = 1 }) => {
      if (auth.studioId) {
        const studioUpdates = await getStudioUpdates({
          pagination: {
            page: pageParam,
            limit: 20,
            search: search.get(),
            studioId: auth.studioId,
          },
          filter: updatesDismissedFilter,
        });
        return studioUpdates;
      }
      return {
        total: 0,
        currentPage: 0,
        totalPages: 0,
        hasMore: false,
        results: [],
      };
    },
    {
      onSuccess: data => {
        hasData.set(true);
        const newFormat = toNewFormat(data);
        const updatedUpdates = updatesOptimistic.backupUpdatedUpdates;
        const newUpdates = updatesOptimistic.backupNewUpdates;

        // Check for newUpdates
        const newUpdateIds = findNewUpdatesToRemove(
          newFormat.results,
          newUpdates,
        );
        dispatch(removeBackUpNewUpdates(newUpdateIds));
        //
        // Check for updatedUpdates
        const updateIds = findUpdatedUpdatesToRemove(
          newFormat.results,
          updatedUpdates,
        );
        dispatch(removeBackUpUpdatedUpdates(updateIds));
      },
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: completedData,
    isLoading: completedIsLoading,
    isError: completedIsError,
    fetchNextPage: completedFetchNextPage,
  } = useInfiniteQuery(
    ['updateStatuses', search.get(), updatesCompletedFilter, updates.updatedAt],
    async ({ pageParam = 1 }) => {
      if (auth.studioId) {
        const studioUpdates = await getStudioUpdates({
          pagination: {
            page: pageParam,
            limit: 20,
            search: search.get(),
            studioId: auth.studioId,
          },
          filter: updatesCompletedFilter,
        });
        return studioUpdates;
      }
      return {
        total: 0,
        currentPage: 0,
        totalPages: 0,
        hasMore: false,
        results: [],
      };
    },
    {
      onSuccess: data => {
        hasData.set(true);
        const newFormat = toNewFormat(data);
        const updatedUpdates = updatesOptimistic.backupUpdatedUpdates;
        const newUpdates = updatesOptimistic.backupNewUpdates;

        // Check for newUpdates
        const newUpdateIds = findNewUpdatesToRemove(
          newFormat.results,
          newUpdates,
        );
        dispatch(removeBackUpNewUpdates(newUpdateIds));
        //
        // Check for updatedUpdates
        const updateIds = findUpdatedUpdatesToRemove(
          newFormat.results,
          updatedUpdates,
        );
        dispatch(removeBackUpUpdatedUpdates(updateIds));
      },
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: assignedToMeData,
    isLoading: assignedToMeIsLoading,
    isError: assignedToMeIsError,
    fetchNextPage: assignedToMeFetchNextPage,
  } = useInfiniteQuery(
    [
      'updateStatuses',
      search.get(),
      updatesPrioritisedFilter,
      'assignedToMe',
      updates.updatedAt,
    ],
    async ({ pageParam = 1 }) => {
      if (auth.studioId) {
        const studioUpdates = await getStudioUpdates({
          pagination: {
            page: pageParam,
            limit: 20,
            search: search.get(),
            studioId: auth.studioId,
          },
          filter: {
            ...updatesPrioritisedFilter,
            assignedToMe: true,
          },
        });
        return studioUpdates;
      }
      return {
        total: 0,
        currentPage: 0,
        totalPages: 0,
        hasMore: false,
        results: [],
      };
    },
    {
      onSuccess: data => {
        hasData.set(true);
        const newFormat = toNewFormat(data);
        const updatedUpdates = updatesOptimistic.backupUpdatedUpdates;
        const newUpdates = updatesOptimistic.backupNewUpdates;

        // Check for newUpdates
        const newUpdateIds = findNewUpdatesToRemove(
          newFormat.results,
          newUpdates,
        );
        dispatch(removeBackUpNewUpdates(newUpdateIds));
        //
        // Check for updatedUpdates
        const updateIds = findUpdatedUpdatesToRemove(
          newFormat.results,
          updatedUpdates,
        );
        dispatch(removeBackUpUpdatedUpdates(updateIds));
      },
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const newSelectedData = useMemo(() => {
    // merge newData
    return toNewFormat(newData);
  }, [newData, updatesOptimistic]);
  const assignedToMeSelectedData = useMemo(() => {
    // merge newData
    const newFormat = toNewFormat(assignedToMeData);
    return newFormat;
  }, [assignedToMeData, updatesOptimistic]);
  const prioritizedSelectedData = useMemo(() => {
    // merge newData
    return toNewFormat(prioritizedData);
  }, [prioritizedData, updatesOptimistic]);
  const completedSelectedData = useMemo(() => {
    // merge newData
    return toNewFormat(completedData);
  }, [completedData, updatesOptimistic]);

  const dismissedSelectedData = useMemo(() => {
    // merge newData
    return toNewFormat(dismissedData);
  }, [dismissedData, updatesOptimistic]);

  if (!page) {
    return <Redirect to="/updates?tab=new" />;
  }

  const onClear = () => {
    startSearch('');
  };
  return (
    <UpdatesContext.Provider
      value={{
        updateStatusId,
      }}
    >
      <Container offsetTop={navigationState.offsetTop.get()}>
        <Head>
          <Title>Updates</Title>

          <Left>
            <UpdatesSearch startSearch={startSearch} onClear={onClear} />

            <FilterButton>
              <Menu filterCount={filterCount} startSearch={startSearch} />
              {filterCount > 0 && <CircleDiv>{filterCount}</CircleDiv>}
            </FilterButton>

            <div data-tip data-for="createUpdateTooltip">
              <CreateButton
                onClick={() => {
                  setOpenCreateUpdates(true);
                }}
                disabled={role !== InviteUserRole.ADMIN}
              >
                <BlueIcon icon="plus" color="#8F95B2" />
              </CreateButton>
              <ReactTooltip
                className="tooltip"
                id="createUpdateTooltip"
                effect="solid"
                place="bottom"
                multiline
                disable={role === InviteUserRole.ADMIN}
              >
                Only admins can create updates.
              </ReactTooltip>
            </div>
          </Left>
        </Head>

        <TabsContainer>
          <Tabs>
            <Item
              onClick={() => {
                history.push('/updates?tab=new');
              }}
            >
              <Label>
                <LabelText isActive={page === 'new'}>New</LabelText>

                <CountComponent count={metricsQuery.data?.new} />
              </Label>

              <Indicator isActive={page === 'new'} />
            </Item>
            <Item
              onClick={() => {
                history.push('/updates?tab=prioritized');
              }}
            >
              <Label>
                <LabelText isActive={page === 'prioritized'}>
                  Prioritized
                </LabelText>

                <CountComponent count={metricsQuery.data?.prioritized} />
              </Label>

              <Indicator isActive={page === 'prioritized'} />
            </Item>
            <Item
              onClick={() => {
                history.push('/updates?tab=completed');
              }}
            >
              <Label>
                <LabelText isActive={page === 'completed'}>Completed</LabelText>

                <CountComponent count={metricsQuery.data?.completed} />
              </Label>

              <Indicator isActive={page === 'completed'} />
            </Item>
            <Item
              onClick={() => {
                history.push('/updates?tab=dismissed');
              }}
            >
              <Label>
                <LabelText isActive={page === 'dismissed'}>Dismissed</LabelText>

                <CountComponent count={metricsQuery.data?.dismissed} />
              </Label>

              <Indicator isActive={page === 'dismissed'} />
            </Item>
            <Item
              onClick={() => {
                history.push('/updates?tab=assinedToMe');
              }}
            >
              <Label>
                <LabelText isActive={page === 'assinedToMe'}>
                  Assigned to me
                </LabelText>

                <CountComponent count={metricsQuery.data?.assignedToMe} />
              </Label>

              <Indicator isActive={page === 'assinedToMe'} />
            </Item>
          </Tabs>
          <Back />
        </TabsContainer>

        <Body show={page === 'new'}>
          <UpdatesNew
            data={newSelectedData.results}
            isLoading={newIsLoading}
            isError={newIsError}
            loadMore={() => newFetchNextPage()}
            hasMore={newSelectedData.hasMore}
          />
        </Body>

        <Body show={page === 'prioritized'}>
          <UpdatesPrioritized
            data={prioritizedSelectedData.results}
            isLoading={prioritizedIsLoading}
            isError={prioritizedIsError}
            loadMore={() => prioritizedFetchNextPage()}
            hasMore={prioritizedSelectedData.hasMore}
          />
        </Body>

        <Body show={page === 'completed'}>
          <UpdatesCompleted
            data={completedSelectedData.results}
            isLoading={completedIsLoading}
            isError={completedIsError}
            loadMore={() => completedFetchNextPage()}
            hasMore={completedSelectedData.hasMore}
          />
        </Body>

        <Body show={page === 'dismissed'}>
          <UpdatesDismissed
            data={dismissedSelectedData.results}
            isLoading={dismissedIsLoading}
            isError={dismissedIsError}
            loadMore={() => dismissedFetchNextPage()}
            hasMore={dismissedSelectedData.hasMore}
          />
        </Body>

        <Body show={page === 'assinedToMe'}>
          <UpdatesAssignedToMe
            data={assignedToMeSelectedData.results}
            isLoading={assignedToMeIsLoading}
            isError={assignedToMeIsError}
            loadMore={() => assignedToMeFetchNextPage()}
            hasMore={assignedToMeSelectedData.hasMore}
          />
        </Body>

        {updateStatusId.get() && (
          <DetailsDialog
            open={!!updateStatusId.get()}
            onClose={() => {
              updateStatusId.set(null);
            }}
            updateId={updateStatusId.get() as string}
          />
        )}

        <CreateUpdatesDialog
          open={openCreateUpdates}
          onClose={() => setOpenCreateUpdates(false)}
        />
      </Container>
    </UpdatesContext.Provider>
  );
};

export { Updates };
